// src/components/Chat/MessageList.js
import React, { useRef, useEffect } from 'react';
import { Loader } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const MessageList = ({ messages, isLoading, style }) => {
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // Custom renderers for markdown
  const markdownComponents = {
    // Make links clickable and open in new tab
    a: ({ href, children }) => (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        style={{ 
          color: '#60a5fa', 
          textDecoration: 'underline',
          wordBreak: 'break-word'
        }}
      >
        {children}
      </a>
    ),
    // Style paragraphs
    p: ({ children }) => (
      <p style={{ margin: '0 0 8px 0' }}>{children}</p>
    ),
    // Style lists
    ul: ({ children }) => (
      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ol>
    ),
    li: ({ children }) => (
      <li style={{ marginBottom: '4px' }}>{children}</li>
    ),
    // Style code
    code: ({ children }) => (
      <code style={{ 
        background: 'rgba(0,0,0,0.3)', 
        padding: '2px 6px', 
        borderRadius: '4px',
        fontSize: '13px'
      }}>
        {children}
      </code>
    ),
    // Style bold
    strong: ({ children }) => (
      <strong style={{ color: '#f1f5f9' }}>{children}</strong>
    )
  };

  return (
    <div
      ref={messagesEndRef}
      style={{
        padding: '16px',
        overflowY: 'auto',
        scrollBehavior: 'smooth',
        ...style
      }}
    >
      {messages.length === 0 ? (
        <div style={{
          textAlign: 'center',
          color: '#94a3b8',
          fontSize: '14px',
          marginTop: '40px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🧠</div>
          <p style={{ marginBottom: '8px' }}>Ask me anything about your business:</p>
          <div style={{
            fontSize: '12px',
            marginTop: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
          }}>
            <span>"Build all modules" • "My name is Tristen" • "Show profit analysis"</span>
            <span>"Add client McDonald's" • "Upload employee data" • "Business intelligence"</span>
          </div>
        </div>
          ) : (
          messages.map(msg => (
          <div key={msg.id} style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: msg.sender === 'user' || msg.role === 'user' ? 'flex-end' : 'flex-start'
          }}>
            <div style={{
              maxWidth: '85%',
              padding: msg.image ? '0' : '12px 16px',
              borderRadius: '12px',
              background: (msg.sender === 'user' || msg.role === 'user')
                ? 'rgba(59, 130, 246, 0.3)'
                : 'rgba(139, 92, 246, 0.3)',
              border: `1px solid ${(msg.sender === 'user' || msg.role === 'user')
                ? 'rgba(59, 130, 246, 0.5)'
                : 'rgba(139, 92, 246, 0.5)'}`,
              color: '#e2e8f0',
              fontSize: '14px',
              lineHeight: '1.5',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word'
            }}>

             {/* ADD IMAGE HANDLING HERE */}
              {msg.image && (
  <div style={{ position: 'relative' }}>
    <style>
      {`
        @keyframes breathe {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 20px rgba(139, 92, 246, 0.3);
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 6px 30px rgba(139, 92, 246, 0.5);
          }
        }
      `}
    </style>
    <img
      src={msg.image}
      alt="Uploaded"
      style={{
        width: '100%',
        borderRadius: '12px',
        display: 'block',
        animation: 'breathe 4s ease-in-out infinite'
      }}
    />
  </div>
)}
              {/* Render content with markdown for clickable links */}
              <ReactMarkdown components={markdownComponents}>
                {msg.content || msg.message || 'No message content'}
              </ReactMarkdown>
              
              {msg.timestamp && (
                <div style={{
                  fontSize: '11px',
                  color: '#94a3b8',
                  marginTop: '4px',
                  opacity: 0.7
                }}>
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </div>
              )}
            </div>
          </div>
        ))
      )}

      {isLoading && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-start',
          marginBottom: '16px'
        }}>
          <div style={{
            padding: '12px 16px',
            borderRadius: '12px',
            background: 'rgba(139, 92, 246, 0.3)',
            border: '1px solid rgba(139, 92, 246, 0.5)',
            color: '#e2e8f0',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Loader size={16} className="animate-spin" />
            Chairman AI is thinking...
          </div>
        </div>
      )}
      {/*CSS animations*/}
      <style>{`
        @keyframes imagePulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(0.98);
          }
        }
      `}</style>
    </div>
  );
};

export default MessageList;
